import {
  建立問題卡片清單 as 建立共享問題卡片清單,
  type 問題卡片,
  type 頁面分類,
  type 處理優先度,
} from "@shared/aiCitationIssueModel";

export type 引用結果狀態 = "已有引用" | "尚無引用";

interface 基本結果 {
  pageClassification?: 頁面分類;
  intentCoverage?: {
    inferredIntents?: Array<{ intentKeyword: string }>;
    trackedMatches?: Array<{
      intentKeyword: string;
      matchedKeyword?: string;
      matchedKeywordId?: number | string;
      matchType?: string;
      aioCitations30d?: number;
      llmCitations30d?: number;
      trackAiOverview?: boolean;
    }>;
    untrackedIntents?: Array<{
      intentKeyword: string;
      suggestedCategory?: string | null;
      suggestedPrimaryIntent?: string;
      reason?: string;
    }>;
    pageEvidence?: {
      citationCount30d?: number;
      providers?: string[];
      matchedCitationKeywords?: string[];
    } | null;
  };
  summary?: {
    keyFindings?: string[];
  };
  recommendations?: {
    mustFix?: string[];
    shouldFix?: string[];
  };
  details?: {
    structure?: { findings?: string[] };
    clarity?: { findings?: string[] };
    trust?: { findings?: string[] };
    consistency?: { findings?: string[] };
    extractability?: { findings?: string[] };
    intentFit?: { findings?: string[] };
    trackingCoverage?: { findings?: string[] };
    aiPerformance?: { findings?: string[] };
  };
}

function 取得頁面分類(result: 基本結果 | null | undefined): 頁面分類 {
  return result?.pageClassification || "內容頁";
}

function 去重字串(items: string[]): string[] {
  return Array.from(new Set(items.filter(Boolean).map((item) => item.trim()).filter(Boolean)));
}

export function 取得引用結果(result: 基本結果 | null | undefined): 引用結果狀態 {
  const count = result?.intentCoverage?.pageEvidence?.citationCount30d ?? 0;
  return count > 0 ? "已有引用" : "尚無引用";
}

export function 建立問題卡片清單(result: 基本結果 | null | undefined): 問題卡片[] {
  if (!result) return [];

  return 建立共享問題卡片清單({
    pageClassification: 取得頁面分類(result),
    issues: 去重字串([
      ...(result.summary?.keyFindings ?? []),
      ...(result.recommendations?.mustFix ?? []),
      ...(result.recommendations?.shouldFix ?? []),
      ...(result.details?.structure?.findings ?? []),
      ...(result.details?.clarity?.findings ?? []),
      ...(result.details?.trust?.findings ?? []),
      ...(result.details?.consistency?.findings ?? []),
      ...(result.details?.extractability?.findings ?? []),
      ...(result.details?.intentFit?.findings ?? []),
      ...(result.details?.trackingCoverage?.findings ?? []),
    ]),
  });
}

export function 取得頁面級問題(result: 基本結果 | null | undefined): 問題卡片[] {
  return 建立問題卡片清單(result).filter((item) => item.scope === "此頁問題");
}

export function 取得優先處理問題(result: 基本結果 | null | undefined): 問題卡片[] {
  return 取得頁面級問題(result).filter((item) => item.priority === "優先處理");
}

export function 取得後續補強問題(result: 基本結果 | null | undefined): 問題卡片[] {
  return 取得頁面級問題(result).filter((item) => item.priority === "後續補強");
}

export function 取得處理優先度(result: 基本結果 | null | undefined): 處理優先度 {
  if (取得優先處理問題(result).length > 0) return "優先處理";
  if (取得後續補強問題(result).length > 0) return "後續補強";
  return "目前穩定";
}

export function 取得主要問題(result: 基本結果 | null | undefined): string {
  const urgent = 取得優先處理問題(result);
  if (urgent.length > 0) return urgent[0].title;
  const followup = 取得後續補強問題(result);
  if (followup.length > 0) return followup[0].title;
  return "目前沒有明顯的此頁問題";
}

export function 取得潛在意圖摘要(result: 基本結果 | null | undefined): string {
  const total = result?.intentCoverage?.inferredIntents?.length ?? 0;
  const tracked = 去重字串(
    (result?.intentCoverage?.trackedMatches ?? []).map((item) => item.intentKeyword)
  ).length;
  const untracked = 去重字串(
    (result?.intentCoverage?.untrackedIntents ?? []).map((item) => item.intentKeyword)
  ).length;
  return `${total} 個潛在關鍵字，已追蹤 ${tracked} / 未追蹤 ${untracked}`;
}
